/**
 * The partially-real suggestion provider.
 *
 * Subdomain availability is the first wizard step wired to the backend: it hits
 * the public, unauthenticated `GET /public/subdomains/available` endpoint. Every
 * other step is still generated by the deterministic {@link ./mock-provider} and
 * delegated to `fallback` here, so they can be swapped to real endpoints (names
 * via Bedrock, source discovery) one at a time with NO change to the wizard UI.
 *
 * Availability is advisory: the authoritative, atomic claim happens at signup.
 * So if the network fails or the endpoint throttles us (429), we fall back to
 * local validation rather than blocking the user on a check that isn't binding.
 */
import { config } from '../config';
import { mockSuggestionProvider } from './mock-provider';
import type {
  NameSuggestion,
  PreviewInput,
  RequestOptions,
  SubdomainAvailability,
  SuggestionInput,
  SuggestionProvider,
} from './provider';

/** True for the AbortController-driven cancellation the debounce hook triggers. */
function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

export function createHttpProvider(
  apiUrl: string = config.apiUrl,
  fallback: SuggestionProvider = mockSuggestionProvider,
): SuggestionProvider {
  async function checkSubdomain(
    name: string,
    options?: RequestOptions,
  ): Promise<SubdomainAvailability> {
    const url = `${apiUrl}/public/subdomains/available?name=${encodeURIComponent(name)}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
        signal: options?.signal ?? null,
      });
      if (!response.ok) {
        // Throttled or server error: don't block the user on a non-binding check.
        return fallback.checkSubdomain(name, options);
      }
      return (await response.json()) as SubdomainAvailability;
    } catch (err) {
      // A cancelled request must propagate so the hook's cleanup ignores it;
      // any other network failure degrades to local validation.
      if (isAbort(err)) {
        throw err;
      }
      return fallback.checkSubdomain(name, options);
    }
  }

  async function suggestNames(
    input: SuggestionInput,
    options?: RequestOptions,
  ): Promise<NameSuggestion[]> {
    const url = `${apiUrl}/public/names?topic=${encodeURIComponent(
      input.topic,
    )}&audience=${input.audience}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
        signal: options?.signal ?? null,
      });
      if (!response.ok) {
        return fallback.suggestNames(input, options);
      }
      const data = (await response.json()) as { names?: NameSuggestion[] };
      // Empty/malformed -> local generator, so the wizard always has options.
      if (!Array.isArray(data.names) || data.names.length === 0) {
        return fallback.suggestNames(input, options);
      }
      return data.names;
    } catch (err) {
      if (isAbort(err)) {
        throw err;
      }
      return fallback.suggestNames(input, options);
    }
  }

  return {
    checkSubdomain,
    suggestNames,
    suggestVoices: (input: SuggestionInput, options?: RequestOptions) =>
      fallback.suggestVoices(input, options),
    suggestSections: (input: SuggestionInput, options?: RequestOptions) =>
      fallback.suggestSections(input, options),
    suggestSources: (input: SuggestionInput, options?: RequestOptions) =>
      fallback.suggestSources(input, options),
    previewIssue: (input: PreviewInput, options?: RequestOptions) =>
      fallback.previewIssue(input, options),
  };
}

/** The app-wide provider: real availability, mock everything else (for now). */
export const httpSuggestionProvider: SuggestionProvider = createHttpProvider();
